/**
 * Reads the value an item should be sorted by, either from an object key or
 * from an accessor function, and normalizes missing values to an empty string.
 * Rows may legitimately have a null value (e.g. a disease that only carries
 * `name_icdc` has `disease: null`), which localeCompare() cannot handle.
 *
 * @param {Object} item
 * @param {string|function(Object): ?string} keyOrAccessor object key or accessor function
 * @returns {string}
 */
const getSortValue = (item, keyOrAccessor) => {
  const value = typeof keyOrAccessor === "function" ? keyOrAccessor(item) : item[keyOrAccessor];

  return value ?? "";
};

/**
 * Compares two items by the given key or accessor, using German collation.
 *
 * @param {Object} a
 * @param {Object} b
 * @param {string|function(Object): ?string} keyOrAccessor object key or accessor function
 * @returns {number}
 */
const compareBy = (a, b, keyOrAccessor) =>
  getSortValue(a, keyOrAccessor).localeCompare(getSortValue(b, keyOrAccessor), "de", {sensitivity: "base"});

/**
 * Reads an indication name, falling back to the ICD-C name for indications
 * that only carry one, matching how they are displayed.
 *
 * @param {Object} item
 * @returns {?string}
 */
const getIndicationName = (item) => item.indications[0]?.name || item.indications[0]?.name_icdc;

/**
 * @param {Array<Object>} array
 * @param {string|function(Object): ?string} keyOrAccessor object key or accessor function
 * @param {string} sortDirection
 * @returns {{sortedArray: Array<Object>, sortedIds: Array<?string>}}
 */
export const sortList = (array, keyOrAccessor, sortDirection = "asc") => {
  // this method is used in a non-mutating way, therefore the non-mutating
  // toSorted() is used instead of sort(), which sorts in place.
  const sortedList = array.toSorted((a, b) => {
    // primary sort by the key or accessor provided
    const primary = compareBy(a, b, keyOrAccessor);
    if (primary !== 0) {
      return sortDirection === "desc" ? -primary : primary;
    }
    // fallback: sort by string id, to ensure consistent order for equal values
    return sortDirection === "desc" ? compareBy(b, a, "id") : compareBy(a, b, "id");
  });

  return {
    sortedArray: sortedList,
    sortedIds: sortedList.map((item) => item.id),
  };
};

export const sortListByUsageAndFam = (array, sortDirection = "asc") => {
  const sortedList = array.toSorted((a, b) => {
    if (a.usage === null) return -1;
    if (b.usage === null) return 1;

    // primary sort by (numeric) usage
    if (a.usage !== b.usage) {
      return sortDirection === "desc" ? b.usage - a.usage : a.usage - b.usage;
    }

    // secondary sort by (alphanumeric) fam
    const famCompare = compareBy(a, b, "fam");
    if (famCompare !== 0) {
      return sortDirection === "desc" ? -famCompare : famCompare;
    }

    // fallback sort by id
    return sortDirection === "desc" ? compareBy(b, a, "id") : compareBy(a, b, "id");
  });

  return {
    sortedArray: sortedList,
    sortedIds: sortedList.map((item) => item.id),
  };
};

export const sortListByIndicationAndFam = (array, sortDirection = "asc") => {
  const sortedList = array.toSorted((a, b) => {
    // primary sort by indication name
    const indicationCompare = compareBy(a, b, getIndicationName);
    if (indicationCompare !== 0) {
      return sortDirection === "desc" ? -indicationCompare : indicationCompare;
    }

    // secondary sort by fam
    const famCompare = compareBy(a, b, "fam");
    if (famCompare !== 0) {
      return sortDirection === "desc" ? -famCompare : famCompare;
    }

    // fallback sort by id
    return sortDirection === "desc" ? compareBy(b, a, "id") : compareBy(a, b, "id");
  });

  return {
    sortedArray: sortedList,
    sortedIds: sortedList.map((item) => item.id),
  };
};
